import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { Ambassadors } from '@sorare/core/src/components/landing/Ambassadors';
import { CommunityBlock } from '@sorare/core/src/components/landing/CommunityBlock';
import { Compete } from '@sorare/core/src/components/landing/Compete';
import { HeroVideo } from '@sorare/core/src/components/landing/HeroVideo';
import type { League } from '@sorare/core/src/components/landing/Leagues';
import { Leagues } from '@sorare/core/src/components/landing/Leagues';
import { Scout } from '@sorare/core/src/components/landing/Scout';
import { Win } from '@sorare/core/src/components/landing/Win';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { LeagueImage } from '@sorare/gameplay/src/components/league/LeagueImage';

import { FAQ } from './FAQ';
import mobileCardsAvif from '../assets/compete/cards-mobile.avif';
import mobileCards from '../assets/compete/cards-mobile.webp';
import competeCardsAvif from '../assets/compete/cards.avif';
import competeCards from '../assets/compete/cards.webp';
import fieldAvif from '../assets/compete/field.avif';
import field from '../assets/compete/field.webp';
import scoutBackgroundAvif from '../assets/scout/background.avif';
import scoutBackground from '../assets/scout/background.webp';
import commonAvif from '../assets/scout/leagues/common.avif';
import common from '../assets/scout/leagues/common.webp';
import limitedAvif from '../assets/scout/leagues/limited.avif';
import limited from '../assets/scout/leagues/limited.webp';
import rareAvif from '../assets/scout/leagues/rare.avif';
import rare from '../assets/scout/leagues/rare.webp';
import superRareAvif from '../assets/scout/leagues/super-rare.avif';
import superRare from '../assets/scout/leagues/super-rare.webp';
import uniqueAvif from '../assets/scout/leagues/unique.avif';
import unique from '../assets/scout/leagues/unique.webp';
import scoutPlayersAvif from '../assets/scout/players.avif';
import scoutPlayers from '../assets/scout/players.webp';
import winBackgroundAvif from '../assets/win/background.avif';
import winBackground from '../assets/win/background.webp';
import winCardsAvif from '../assets/win/card.avif';
import winCards from '../assets/win/card.webp';
import cashAvif from '../assets/win/cash.avif';
import cash from '../assets/win/cash.webp';
import ticketsAvif from '../assets/win/tickets.avif';
import tickets from '../assets/win/tickets.webp';
import vipAvif from '../assets/win/vip.avif';
import vip from '../assets/win/vip.webp';

const Root = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--quadruple-unit);
  overflow: hidden;
  position: relative;
  padding-bottom: var(--double-unit);
  &.showFAQ {
    padding-bottom: calc(8 * var(--unit));
  }
  @media ${tabletAndAbove} {
    gap: calc(8 * var(--unit));
  }
`;
const Hero = styled.div`
  &.showOtherLeagues {
    @media ${tabletAndAbove} {
      margin-bottom: calc(var(--unit) * 12);
    }
  }
`;
const Players = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex: 1;
  background-image: var(--players);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center top -5%;
  min-width: 100%;
  max-width: 100%;
  aspect-ratio: 746/481;
  @media ${laptopAndAbove} {
    height: 480px;
    background-position: right center;
  }
`;

const ImgWrapper = styled.div`
  padding: 0 var(--unit);
`;

const assets = {
  scout: {
    players: {
      avif: scoutPlayersAvif,
      pictureUrl: scoutPlayers,
    },
    background: {
      avif: scoutBackgroundAvif,
      pictureUrl: scoutBackground,
    },
  },
  compete: {
    field: {
      avif: fieldAvif,
      pictureUrl: field,
    },
  },
  win: {
    cards: {
      avif: winCardsAvif,
      pictureUrl: winCards,
    },
  },
};

const leagueAssets = [
  {
    id: 'common',
    pictureUrl: common,
    avif: commonAvif,
  },
  {
    id: 'limited',
    pictureUrl: limited,
    avif: limitedAvif,
  },
  {
    id: 'rare',
    pictureUrl: rare,
    avif: rareAvif,
  },
  {
    id: 'super rare',
    pictureUrl: superRare,
    avif: superRareAvif,
  },
  {
    id: 'unique',
    pictureUrl: unique,
    avif: uniqueAvif,
  },
];

type Props = {
  showFAQ?: boolean;
  leagues?: League[];
  leaderboards: So5LeaderboardType[];
  league?: ReactNode;
};

export const EarlyAccess = ({
  showFAQ,
  leagues,
  leaderboards,
  league,
}: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  return (
    <Root className={classNames({ extraBottomPadding: !showFAQ })}>
      <Hero className={classNames({ showOtherLeagues: !!leagues })}>
        <HeroVideo
          league={league}
          title={
            <FormattedMessage
              id="mlb.landing.EarlyAccess.title"
              defaultMessage="A New Era of Fantasy Baseball"
            />
          }
          subtitle={
            <FormattedMessage
              id="mlb.landing.EarlyAccess.subtitle"
              defaultMessage="Think you know ball? Create your fantasy club now and compete for VIP tickets, signed jerseys and more."
            />
          }
        />
        {!!leagues && <Leagues leagues={leagues} />}
      </Hero>
      <Scout
        assets={{
          players: assets.scout.players,
          background: assets.scout.background,
        }}
        title={
          <FormattedMessage
            id="nba.landing.EarlyAccess.scout.title"
            defaultMessage="Build"
          />
        }
        description={
          <FormattedMessage
            id="mlb.landing.EarlyAccess.Scout.description"
            defaultMessage="Create your ultimate collection of official MLB digital player cards. Trade, buy, and sell them on the marketplace to keep your edge."
          />
        }
        ctaLabel={<FormattedMessage {...glossary.joinForFree} />}
        players={
          <Players
            style={
              {
                '--players': `image-set(url(${assets.scout.players.avif}) type('image/avif'), url(${assets.scout.players.pictureUrl}) type('image/webp'))`,
              } as CSSProperties
            }
          />
        }
      >
        {leagueAssets.map(({ id, ...assetProps }) => (
          <LeagueImage key={id} {...assetProps} headerUrl={null} />
        ))}
      </Scout>
      <Compete
        assets={{
          field: {
            avif: fieldAvif,
            pictureUrl: field,
          },
        }}
        title={
          <FormattedMessage
            id="landing.EarlyAccess.Compete.title"
            defaultMessage="Compete"
          />
        }
        description={
          <FormattedMessage
            id="mlb.landing.EarlyAccess.Compete.description"
            defaultMessage="Set 7 player lineups and compete in daily & bi-weekly fantasy baseball competitions."
          />
        }
        img={
          <ImgWrapper>
            <picture>
              <source
                srcSet={isTabletAndAbove ? competeCardsAvif : mobileCardsAvif}
                type="image/avif"
              />
              <Img
                src={isTabletAndAbove ? competeCards : mobileCards}
                fetchPriority="low"
                width="1163"
                height="302"
              />
            </picture>
          </ImgWrapper>
        }
      />
      <Container>
        <Win
          assets={{
            ...assets.win,
            background: {
              avif: winBackgroundAvif,
              pictureUrl: winBackground,
            },
          }}
          title={
            <FormattedMessage
              id="nba.EarlyAccess.Win.title"
              defaultMessage="Become a Legend"
            />
          }
          description={
            <FormattedMessage
              id="mlb.EarlyAccess.Win.subtitle"
              defaultMessage="Cement your legacy as the best Manager in the Sorare community. Show off your baseball IQ and build the ultimate collection."
            />
          }
          ctaLabel={<FormattedMessage {...glossary.joinForFree} />}
          rewards={[
            {
              id: 'vip',
              avif: vipAvif,
              pictureUrl: vip,
              width: 120,
              height: 224,
              label: (
                <FormattedMessage
                  id="nba.landing.EarlyAccess.Win.vip"
                  defaultMessage="Vip Experiences"
                />
              ),
            },
            {
              id: 'card',
              avif: winCardsAvif,
              pictureUrl: winCards,
              width: 104,
              height: 192,
              label: (
                <FormattedMessage
                  id="nba.landing.EarlyAccess.Win.cards"
                  defaultMessage="Playable Cards"
                />
              ),
            },
            {
              id: 'cash',
              avif: cashAvif,
              pictureUrl: cash,
              width: 224,
              height: 248,
              label: <FormattedMessage {...glossary.cashRewards} />,
            },
            {
              id: 'tickets',
              avif: ticketsAvif,
              pictureUrl: tickets,
              width: 200,
              height: 128,
              label: (
                <FormattedMessage
                  id="nba.landing.EarlyAccess.Win.tickets"
                  defaultMessage="Game Tickets"
                />
              ),
            },
          ]}
        />
      </Container>
      <CommunityBlock
        leaderboards={leaderboards.map(leaderboardType => ({
          sport: Sport.BASEBALL,
          leaderboardType,
        }))}
      />
      <Container>
        <Ambassadors />
      </Container>
      {!!showFAQ && <FAQ />}
    </Root>
  );
};
